import { ComponentProps, FunctionComponent } from "react";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { Alert, Box, CardContent, Container, Link, Skeleton, Stack } from "@mui/material";
import { useSemanticCatalogEnabled } from "catalog/hooks/useCatalogEnabled";
import CardGrid from "~/components/CardGrid";
import CT from "~/components/ClampedTypography";
import InteractiveCard, { IconContainer } from "~/components/InteractiveCard";
import PageHeader from "~/components/PageHeader";
import ScrollableTabContent from "~/components/ScrollableTabContent";
import { useDataBrowserReports } from "~/hooks/useDataBrowserReports";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { ROUTE_404 } from "~/router/constants";

const { VITE_DATA_DASHBOARD_URL, VITE_SIGMA_REPORT_IDS } = import.meta.env;

const title = "Dashboards";

interface ReportTileProps {
  label: string;
  description: string;
  href: string;
  icon?: FunctionComponent<ComponentProps<"svg">>;
  iconColor?: string;
  backgroundColor?: string;
}

const Loading = () => (
  <>
    {Array.from({ length: 4 }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={175} />
    ))}
  </>
);

const ReportTile = ({ href, icon: Icon, iconColor, label, description }: ReportTileProps) => {
  return (
    <Link component={RouterLink} underline="none" to={href}>
      <InteractiveCard>
        <CardContent sx={{ gap: 1, height: "200px", px: 3, pt: 2.5 }}>
          {Icon && (
            <IconContainer>
              <Icon color={iconColor ?? "#31323C"} />
            </IconContainer>
          )}
          <CT variant="body1" fontWeight={600} lineHeight={1.2}>
            {label}
          </CT>
          <CT
            variant="body2"
            sx={{
              color: "grey.700",
              display: "flex",
              flex: 1,
              flexDirection: "column",
              fontSize: 12,
              justifyContent: "flex-end",
              lineHeight: 1.3,
              opacity: 0.7,
            }}
          >
            {description}
          </CT>
        </CardContent>
      </InteractiveCard>
    </Link>
  );
};

const Reports = () => {
  useDocumentTitle(title);
  // Note: App header is managed by DataBrowser parent component
  const { reportTiles, loading, error } = useDataBrowserReports();

  const semanticCatalogEnabled = useSemanticCatalogEnabled();
  if (!VITE_SIGMA_REPORT_IDS && !VITE_DATA_DASHBOARD_URL && !semanticCatalogEnabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  return (
    <ScrollableTabContent bgcolor="common.dimmedPageBackground">
      <Container maxWidth="lg" sx={{ pt: 2, pb: 3 }}>
        <Stack gap={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" minHeight={40}>
            <PageHeader lineHeight={1.8}>Dashboards</PageHeader>
          </Box>
          {error && (
            <Alert severity="error" variant="outlined">
              Unable to load data
            </Alert>
          )}
          <CardGrid>
            {loading && !error ? (
              <Loading />
            ) : (
              reportTiles.map((report) => <ReportTile key={report.label} {...report} />)
            )}
          </CardGrid>
        </Stack>
      </Container>
    </ScrollableTabContent>
  );
};

export default Reports;
